import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Button, Card, Col, DatePicker, Divider, Form, Input, message, Modal, Row, Select, Space, Table, Tooltip, TreeSelect, Typography, theme } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormItemStyle, filterFormGridLayout, computeButtonColLayout } from '@utils/property';
import { getSelectionListApi, updateCasesStatusApi, getProjectsAllApi, getProjestsTopicsListApi } from '@services/Selection';
import { getMemberAuthInfoApi } from '@services/UCenter';
import './CasesList.less';
import { selectionActions } from '../store/selectionSlice';
import { formatAiReviewRejectReasons, isProjectAiReviewEnabled } from '../aiReviewUtils';
import PageHeader from '@components/PageHeader/PageHeader';
const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

const { setCaseFilter: setFilter, setCaseCursor: setCursor } = selectionActions;

const CommentIcon = (
    <span className="anticon" style={{ color: '#ffa940' }}>
        <i className="bi bi-chat-dots-fill"></i>
    </span>
);

const CASES_FILTER_BASE_FIELD_COUNT = 7;

function computeCasesFilterFieldCounts({ expanded, projectId, topicId, topics, directions, projectsListData }) {
    const showTopic = !!projectId && !!topics?.length;
    const showDirection = !!topicId && !!directions?.length;
    const project = projectsListData?.find(p => p.id === projectId);
    const showContactsConfirmed = !projectId || (project != null && Number(project.contactsConfirmed) === 1);
    const showAiReviewStat = !projectId || isProjectAiReviewEnabled(project);
    const collapsedFieldCount = CASES_FILTER_BASE_FIELD_COUNT + (showTopic ? 1 : 0) + (showDirection ? 1 : 0);
    const expandedFieldCount = collapsedFieldCount + 3 + (showAiReviewStat ? 1 : 0) + (showContactsConfirmed ? 1 : 0);

    return {
        visibleFieldCount: expanded ? expandedFieldCount : collapsedFieldCount,
        collapsedFieldCount,
        expandedFieldCount,
    };
}

export default function CasesList() {
    const navigate = useNavigate();
    const { token } = theme.useToken();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.selection.case.filter);
    const cursor = useSelector(state => state.selection.case.cursor);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const [form] = Form.useForm();
    const watchedProjectId = Form.useWatch('projectId', form);

    const [topics, setTopics] = useState([]);
    const [directions, setDirections] = useState([]);

    // 获取列表数据
    const [data, setData] = useState([]);
    const [projectsListData, setProjectsListData] = useState([]);
    const [total, setTotal] = useState(0);

    // 筛选面板展开/收起
    const [expanded, setExpanded] = useState(false);

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const showContactsConfirmedFilter = useMemo(() => {
        if (!watchedProjectId) {
            return true;
        }
        const project = projectsListData?.find(p => p.id === watchedProjectId);
        return project != null && Number(project.contactsConfirmed) === 1;
    }, [watchedProjectId, projectsListData]);

    /** 未选项目时展示；选中项目时仅当该项目开启 AI 初审时展示 */
    const showAiReviewStatFilter = useMemo(() => {
        if (!watchedProjectId) {
            return true;
        }
        const project = projectsListData?.find(p => p.id === watchedProjectId);
        return isProjectAiReviewEnabled(project);
    }, [watchedProjectId, projectsListData]);
    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onFinish = values => {
        const next = {
            ...filter,
            ...values,
            page: 1,
        };
        if (values.contactsConfirmed === undefined) {
            delete next.contactsConfirmed;
        }
        const project = projectsListData?.find(p => p.id === next.projectId);
        if (next.projectId != null && project != null && Number(project.contactsConfirmed) !== 1) {
            delete next.contactsConfirmed;
        }
        if (next.projectId != null && !isProjectAiReviewEnabled(project)) {
            delete next.aiReviewStat;
        }
        dispatch(setFilter(next));
    };

    const onReset = () => {
        form.resetFields();
        setExpanded(false);

        const next = {
            ...filter,
            ...form.getFieldsValue(),
            page: 1,
        };
        if (next.contactsConfirmed === undefined) {
            delete next.contactsConfirmed;
        }
        const project = projectsListData?.find(p => p.id === next.projectId);
        if (next.projectId != null && project != null && Number(project.contactsConfirmed) !== 1) {
            delete next.contactsConfirmed;
        }
        if (next.projectId != null && !isProjectAiReviewEnabled(project)) {
            delete next.aiReviewStat;
        }
        dispatch(setFilter(next));
    };

    const filterOption = (input, option) => {
        const { name } = option.props.data;
        return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    // 递归处理topics数据，禁用非叶子节点
    const processTopicsData = data => {
        return data.map(item => {
            const hasChildren = item.children && item.children.length > 0;
            return {
                ...item,
                disabled: hasChildren, // 有子节点则禁用
                children: hasChildren ? processTopicsData(item.children) : undefined,
            };
        });
    };

    const onChangeProject = projectId => {
        if (projectId) {
            const project = projectsListData?.find(p => p.id === projectId);

            // 是否开启负责人确认
            const contactsFilterOn = project != null && Number(project.contactsConfirmed) === 1;

            // 是否开启 AI 初审
            const aiReviewFilterOn = project != null && isProjectAiReviewEnabled(project);

            setDirections([]);

            form.setFieldsValue({
                topicIds: undefined,
                directionId: undefined,
                ...(!contactsFilterOn ? { contactsConfirmed: undefined } : {}),
                ...(!aiReviewFilterOn ? { aiReviewStat: undefined } : {}),
            });

            const merged = {
                ...filter,
                ...form.getFieldsValue(),
                projectId,
                topicIds: undefined,
                directionId: undefined,
                page: 1,
            };
            if (!contactsFilterOn) {
                delete merged.contactsConfirmed;
            }
            if (!aiReviewFilterOn) {
                delete merged.aiReviewStat;
            }
            dispatch(setFilter(merged));

            getProjestsTopicsListApi({ projectId })
                .then(topics => {
                    if (topics.status_code === 200) {
                        setTopics(processTopicsData(topics.data));
                    } else {
                        setTopics([]);
                    }
                })
                .catch(() => {
                    setTopics([]);
                });
        } else {
            setTopics([]);
            setDirections([]);

            form.setFieldsValue({
                projectId: undefined,
                topicIds: undefined,
                directionId: undefined,
            });

            dispatch(
                setFilter({
                    ...filter,
                    ...form.getFieldsValue(),
                    projectId: undefined,
                    topicIds: undefined,
                    directionId: undefined,
                    page: 1,
                }),
            );
        }
    };

    const getDirectionsFromTopic = (topicList, topicId) => {
        let directions = [];

        if (topicId && topicList && topicList.length) {
            topicList.forEach(topic => {
                if (topic.id === topicId) {
                    directions = topic.topicDirectionList || [];
                } else {
                    if (topic.children?.length) {
                        topic.children.forEach(t => {
                            if (t.id === topicId) {
                                directions = t.topicDirectionList || [];
                            }
                        });
                    }
                }
            });
        }

        return directions;
    };

    const onChangeTopic = topicId => {
        console.log('onChangeTopic', topicId, 'directions', getDirectionsFromTopic(topicId));
        if (topicId) {
            setDirections(getDirectionsFromTopic(topics, topicId));
            form.setFieldsValue({
                topicIds: topicId,
                directionId: undefined,
            });

            dispatch(
                setFilter({
                    ...filter,
                    ...form.getFieldsValue(),
                    topicIds: topicId,
                    directionId: undefined,
                    page: 1,
                }),
            );
        } else {
            form.setFieldsValue({
                topicIds: undefined,
            });

            dispatch(
                setFilter({
                    ...filter,
                    ...form.getFieldsValue(),
                    directionId: undefined,
                    page: 1,
                }),
            );
        }
    };

    const goCases = row => {
        if (row.id !== cursor) {
            dispatch(setCursor(row.id));
        }

        let caseName;
        try {
            caseName = JSON.parse(row.fieldValue)?.title;
        } catch {
            // ignore
        }
        navigate(`/selection/cases/${row.id}`, caseName ? { state: { caseName } } : undefined);
    };

    // 编辑
    const edit = row => {
        if (row.id !== cursor) {
            dispatch(setCursor(row.id));
        }

        let caseName;
        try {
            caseName = JSON.parse(row.fieldValue)?.title;
        } catch {
            // ignore
        }
        navigate(`/selection/cases/edit/${row.id}`, {
            state: {
                slug: row.slug,
                memcard: row.memcard,
                status: row.status,
                caseName,
            },
        });
    };

    useEffect(() => {
        getProjectsAllApi({}).then(res => {
            if (res?.status_code === 200) {
                setProjectsListData(res.data);
            }
        });
    }, []);

    useEffect(() => {
        document.title = '实践管理-医评议';
    }, []);

    // 获取实践列表数据
    const getData = () => {
        const sendValue = { ...filter };
        if (filter?.createdAt) {
            sendValue.createdAtBegin = dayjs(filter.createdAt[0]).format('YYYY-MM-DD HH:mm:ss');
            if (typeof sendValue.createdAt[0] === 'string') {
                sendValue.createdAt[0] = dayjs(filter.createdAt[0].toString());
            }
        }

        if (filter?.createdAt) {
            sendValue.createdAtEnd = dayjs(filter.createdAt[1]).format('YYYY-MM-DD HH:mm:ss');
            if (typeof sendValue.createdAt[1] === 'string') {
                sendValue.createdAt[1] = dayjs(filter.createdAt[1].toString());
            }
        }

        if (filter?.reviewedAt) {
            sendValue.reviewedAtBegin = dayjs(filter.reviewedAt[0]).format('YYYY-MM-DD HH:mm:ss');
            if (typeof sendValue.reviewedAt[0] === 'string') {
                sendValue.reviewedAt[0] = dayjs(filter.reviewedAt[0].toString());
            }
        }

        if (filter?.reviewedAt) {
            sendValue.reviewedAtEnd = dayjs(filter.reviewedAt[1]).format('YYYY-MM-DD HH:mm:ss');
            if (typeof sendValue.reviewedAt[1] === 'string') {
                sendValue.reviewedAt[1] = dayjs(filter.reviewedAt[1].toString());
            }
        }

        if (filter?.reviewStatus && filter.reviewStatus === -1) {
            delete sendValue.reviewStatus;
        }

        if (filter?.aiReviewStat === undefined || filter?.aiReviewStat === null || filter?.aiReviewStat === -1) {
            delete sendValue.aiReviewStat;
        }

        if (filter?.status && filter.status === -1) {
            delete sendValue.status;
        }

        if (filter?.contactsConfirmed === undefined || filter?.contactsConfirmed === null) {
            delete sendValue.contactsConfirmed;
        }
        const projectForFilter = projectsListData?.find(p => p.id === filter?.projectId);
        if (filter?.projectId != null && projectForFilter != null && Number(projectForFilter.contactsConfirmed) !== 1) {
            delete sendValue.contactsConfirmed;
        }
        if (filter?.projectId != null && !isProjectAiReviewEnabled(projectForFilter)) {
            delete sendValue.aiReviewStat;
        }

        form.setFieldsValue(sendValue);

        getSelectionListApi({ ...sendValue, size: sendValue.pageSize }).then(res => {
            if (res && res.status_code === 200) {
                const records = res.data.records;
                setTotal(res.data.total);

                // 收集所有不重复的 memcard
                const memcards = [...new Set(records.map(item => item.memcard).filter(Boolean))];

                // 如果有 memcard，则查询用户认证信息
                if (memcards.length > 0) {
                    getMemberAuthInfoApi({ data: JSON.stringify({ memCards: memcards.join(',') }) })
                        .then(authRes => {
                            console.log('authRes', authRes);
                            if (authRes && authRes.count === 0 && authRes.data?.list) {
                                // 将认证信息映射为 memCard -> 用户信息的对象
                                const authInfoMap = {};
                                authRes.data.list.forEach(authInfo => {
                                    authInfoMap[authInfo.memCard] = authInfo;
                                });

                                // 将认证信息合并到实践数据中
                                const enrichedData = records.map(item => {
                                    const authInfo = authInfoMap[item.memcard];
                                    let displayName = item.nickName;

                                    if (authInfo) {
                                        // 个人专业认证已通过：memType === 1 && memIsyijiehui === 1
                                        if (authInfo.memType === 1 && authInfo.memIsyijiehui === 1) {
                                            // 个人专业认证已通过，优先取姓名
                                            displayName = authInfo.memUsername || authInfo.memNickname || item.nickName;
                                        }
                                        // 机构认证已通过：memType === 2 && memAuthStatus === 2
                                        else if (authInfo.memType === 2 && authInfo.memAuthStatus === 2) {
                                            // 机构认证已通过，优先取机构名称
                                            displayName = authInfo.orgname || authInfo.memNickname || item.nickName;
                                        }
                                        // 未通过审核，取昵称
                                        else {
                                            displayName = authInfo.memNickname || item.nickName;
                                        }
                                    }

                                    return {
                                        ...item,
                                        authInfo,
                                        displayName,
                                    };
                                });

                                console.log('enrichedData', enrichedData);

                                setData(enrichedData);
                            } else {
                                // 如果查询失败，使用原有数据
                                setData(records);
                            }
                        })
                        .catch(() => {
                            // 查询出错时，使用原有数据
                            setData(records);
                        });
                } else {
                    // 如果没有 memcard，直接使用原有数据
                    setData(records);
                }
            }
        });
    };

    useEffect(() => {
        const shouldExpand = (filter.status !== undefined && filter.status !== -1) || filter.contactsConfirmed !== undefined || !!filter.memCard || !!filter.nickName || !!filter.mobile || (filter.aiReviewStat !== undefined && filter.aiReviewStat !== null && filter.aiReviewStat !== -1);
        if (shouldExpand) {
            setExpanded(true);
        }
    }, [filter.contactsConfirmed, filter.status, filter.memCard, filter.nickName, filter.mobile, filter.aiReviewStat]);

    useEffect(() => {
        getData();
        console.log('filter', filter);
        if (filter?.projectId) {
            getProjestsTopicsListApi({ projectId: filter.projectId }).then(res => {
                if (res?.status_code === 200) {
                    const topicList = processTopicsData(res.data);
                    setTopics(topicList);

                    if (filter?.topicIds) {
                        setDirections(getDirectionsFromTopic(topicList, filter.topicIds));
                    }
                }
            });
        }
    }, [filter, projectsListData]);

    const onUpdateCases = (status, item) => {
        if (item.id !== cursor) {
            dispatch(setCursor(item.id));
        }
        if (status === 2) {
            updateCasesStatusApi({ id: item.id, status: status }).then(res => {
                if (res.status_code === 200) {
                    message.success(res.message);
                    getData();
                }
            });
        } else {
            Modal.confirm({
                title: '提示',
                content: '实践下架后用户端将无法查看，确定下架该实践吗？',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    updateCasesStatusApi({ id: item.id, status: status }).then(res => {
                        if (res.status_code === 200) {
                            message.success(res.message);
                            getData();
                        }
                    });
                },
            });
        }
    };

    const columns = [
        {
            title: '实践名称',
            dataIndex: 'fieldValue',
            width: 300,
            render: (fieldValue, row) => (
                <>
                    <span>{JSON.parse(fieldValue).title}</span>
                    {!!row.duplicateError && (
                        <Text type="danger" style={{ marginLeft: 4, padding: '0 4px' }}>
                            查重异常
                        </Text>
                    )}
                    <br />
                    <Text type="secondary" style={{ verticalAlign: 'middle' }}>
                        #{row.id}
                    </Text>
                </>
            ),
        },
        {
            title: '项目主题',
            dataIndex: 'projectName',
            width: 300,
            render: (_, row) => (
                <>
                    {row.projectName}
                    <br />
                    <Text type="secondary">主题：{row.topicName}</Text>
                    <br />
                    {row.directionName && <Text type="secondary">主题方向：{row.directionName}</Text>}
                </>
            ),
        },
        { title: '申报单位', dataIndex: 'hospitalName', width: 300 },
        {
            title: '申报人',
            dataIndex: 'username',
            width: 220,
            render: (_, row) => (
                <>
                    <Tooltip
                        title={
                            <>
                                用户memcard：<small>{row.memcard}</small>
                                <br />
                                用户昵称：{row.nickName}
                                <br />
                                手机：{row.mobile}
                                {row.authInfo && (
                                    <>
                                        {row.authInfo.memType === 1 && row.authInfo.memIsyijiehui === 1 && (
                                            <>
                                                <br />
                                                <span>认证类型：个人专业认证</span>
                                                <br />
                                                {row.authInfo.memUsername && <>姓名：{row.authInfo.memUsername}</>}
                                            </>
                                        )}
                                        {row.authInfo.memType === 2 && row.authInfo.memAuthStatus === 2 && (
                                            <>
                                                <br />
                                                <span>认证类型：机构认证</span>
                                                <br />
                                                {row.authInfo.orgname && <>机构名称：{row.authInfo.orgname}</>}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        }
                    >
                        <span>{row.displayName || row.nickName}</span>
                    </Tooltip>
                    <br />
                    <Text type="secondary" style={{ verticalAlign: 'middle' }}>
                        <small>提交时间：{row.createdAt}</small>
                    </Text>
                    {row.createdAt !== row.updatedAt && row.updatedAt && (
                        <>
                            <br />
                            <Text type="secondary" style={{ verticalAlign: 'middle' }}>
                                <small>最后提交：{row.updatedAt}</small>
                            </Text>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '实践负责人确认',
            dataIndex: 'contactsConfirmed',
            width: 160,
            render: (contactsConfirmed, row) => {
                const project = projectsListData?.find(p => p.id === row.projectId);
                const projectContactsConfirmOn = project != null && Number(project.contactsConfirmed) === 1;
                if (!projectContactsConfirmOn) {
                    return <Text type="secondary">&ndash;</Text>;
                }
                const val = Number(contactsConfirmed);
                return (
                    <>
                        {val === 0 && <span>待确认</span>}
                        {val === 1 && <span>已确认</span>}
                    </>
                );
            },
        },
        {
            title: 'AI初审',
            dataIndex: 'aiReviewStat',
            width: 100,
            render: (aiReviewStat, row) => {
                const project = projectsListData?.find(p => p.id === row.projectId);
                if (!isProjectAiReviewEnabled(project)) {
                    return <Text type="secondary">&ndash;</Text>;
                }
                const stat = Number(aiReviewStat);
                const aiRejectReasonText = formatAiReviewRejectReasons(row.ai_review_msg ?? row.aiReviewMsg);
                return (
                    <>
                        {stat === 0 && <span>待审核</span>}
                        {stat === 1 && <span>审核中</span>}
                        {stat === 2 && <span>已通过</span>}
                        {stat === 3
                            && (aiRejectReasonText ? (
                                <Tooltip color="#ffffff" title={<span className="ToolRejectReason">{aiRejectReasonText}</span>}>
                                    <Text type="warning">存在异常 {CommentIcon}</Text>
                                </Tooltip>
                            ) : (
                                <span>存在异常</span>
                            ))}
                    </>
                );
            },
        },
        {
            title: '审核状态',
            dataIndex: 'reviewedStatus',
            width: 100,
            render: (reviewedStatus, row) => (
                <>
                    {reviewedStatus === 0 && row.status === 0 && <Text type="secondary">&ndash;</Text>}
                    {reviewedStatus === 0 && row.status > 0 && <span>待审核</span>}
                    {reviewedStatus === 1 && <Text type="success">已通过</Text>}
                    {reviewedStatus === 2 && (
                        <Tooltip color="#ffffff" title={<span className="ToolRejectReason">{row.rejectReason}</span>}>
                            <Text type="warning">已驳回 {CommentIcon}</Text>
                        </Tooltip>
                    )}
                </>
            ),
        },
        {
            title: '审核人',
            dataIndex: 'reviewedBy',
            width: 160,
            render: (_, row) => (
                <>
                    {row.reviewedStatus !== 0 && (
                        <>
                            <span>{row.reviewedName}</span>
                            <br />
                            <Text type="secondary" style={{ verticalAlign: 'middle' }}>
                                <small>{row.reviewedAt}</small>
                            </Text>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '发布状态',
            dataIndex: 'status',
            width: 100,
            render: (status, _row) => (
                <>
                    {status === 0 && <span>草稿</span>}
                    {status === 1 && <span>未发布</span>}
                    {status === 2 && <Text type="success">已上架</Text>}
                    {status === 3 && <Text type="warning">已下架</Text>}
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 160,
            fixed: 'right',
            render: (_, row) => (
                <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                    <Button size="small" type="link" onClick={() => edit(row)}>
                        编辑
                    </Button>
                    <Button size="small" type="link" onClick={() => goCases(row)}>
                        实践详情
                    </Button>
                    {row.status === 2 && (
                        <Button size="small" type="link" onClick={() => onUpdateCases(3, row)}>
                            下架
                        </Button>
                    )}
                    {row.status === 3 && (
                        <Button size="small" type="link" onClick={() => onUpdateCases(2, row)}>
                            上架
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container list-page-container selection-cases-page">
            <PageHeader title="实践管理" breadcrumb={[{ label: '医评议' }, { label: '实践管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="selection-cases-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} onFinish={onFinish} form={form} initialValues={{ reviewStatus: -1, status: -1 }} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="实践名称或ID" name="title" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="实践名称关键字或ID" autoComplete="off" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="评议项目" name="projectId" style={filterFormItemStyle}>
                                    <Select placeholder="请选择评议项目" showSearch={{ optionFilterProp: 'children', filterOption: filterOption }} onChange={value => onChangeProject(value)} allowClear>
                                        {projectsListData?.map(item => (
                                            <Option key={item.id} value={item.id} data={item}>
                                                {item.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Form.Item noStyle shouldUpdate>
                                {({ getFieldValue }) =>
                                    !!getFieldValue('projectId')
                                    && !!topics?.length && (
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="申报主题" name="topicIds" style={filterFormItemStyle}>
                                                <TreeSelect placeholder="请选择申报主题" treeData={topics} fieldNames={{ label: 'name', value: 'id' }} treeDefaultExpandAll showSearch={{ treeNodeFilterProp: 'name' }} allowClear onChange={value => onChangeTopic(value)} />
                                            </Form.Item>
                                        </Col>
                                    )
                                }
                            </Form.Item>

                            <Form.Item noStyle shouldUpdate>
                                {({ getFieldValue }) =>
                                    !!getFieldValue('topicIds')
                                    && !!directions?.length && (
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="主题方向" name="directionId" style={filterFormItemStyle}>
                                                <Select placeholder="请选择主题方向" options={directions} fieldNames={{ label: 'description', value: 'id' }} allowClear />
                                            </Form.Item>
                                        </Col>
                                    )
                                }
                            </Form.Item>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="实践申报机构" name="hospitalName" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="申报机构名称" autoComplete="off" />
                                </Form.Item>
                            </Col>
                            {expanded && (
                                <>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="用户memCard" name="memCard" style={filterFormItemStyle}>
                                            <Input allowClear placeholder="投稿用户memCard" autoComplete="off" />
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="用户昵称" name="nickName" style={filterFormItemStyle}>
                                            <Input allowClear placeholder="投稿用户昵称" autoComplete="off" />
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="用户手机" name="mobile" style={filterFormItemStyle}>
                                            <Input allowClear placeholder="投稿用户手机" autoComplete="off" />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="实践提效时间" name="createdAt" style={filterFormItemStyle}>
                                    <RangePicker className="ProjectRangPicker" allowClear showTime />
                                </Form.Item>
                            </Col>

                            {expanded && showAiReviewStatFilter && (
                                <Col {...filterFormGridLayout}>
                                    <Form.Item label="AI初审状态" name="aiReviewStat" style={filterFormItemStyle}>
                                        <Select
                                            placeholder="请选择AI初审状态"
                                            allowClear
                                            options={[
                                                { value: 0, label: '待审核' },
                                                { value: 1, label: '审核中' },
                                                { value: 2, label: '已通过' },
                                                { value: 3, label: '存在异常' },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                            )}

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="审核时间" name="reviewedAt" style={filterFormItemStyle}>
                                    <RangePicker className="ProjectRangPicker" allowClear showTime />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="审核状态" name="reviewStatus" style={filterFormItemStyle}>
                                    <Select
                                        placeholder="请选择审核状态"
                                        options={[
                                            { value: -1, label: '全部' },
                                            { value: 0, label: '待审核' },
                                            { value: 1, label: '审核通过' },
                                            { value: 2, label: '审核驳回' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="发布状态" name="status" style={filterFormItemStyle}>
                                    <Select
                                        placeholder="请选择发布状态"
                                        options={[
                                            { value: -1, label: '全部（不含草稿）' },
                                            { value: 0, label: '草稿' },
                                            { value: 1, label: '未发布' },
                                            { value: 2, label: '已上架' },
                                            { value: 3, label: '已下架' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            {expanded && showContactsConfirmedFilter && (
                                <Col {...filterFormGridLayout}>
                                    <Form.Item label="负责人确认状态" name="contactsConfirmed" style={filterFormItemStyle}>
                                        <Select
                                            allowClear
                                            placeholder="请选择负责人确认状态"
                                            options={[
                                                { value: 0, label: '待确认' },
                                                { value: 1, label: '已确认' },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                            )}

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.projectId !== cur.projectId || prev.topicIds !== cur.topicIds}>
                                {({ getFieldValue }) => {
                                    const { visibleFieldCount, collapsedFieldCount, expandedFieldCount } = computeCasesFilterFieldCounts({
                                        expanded,
                                        projectId: getFieldValue('projectId'),
                                        topicId: getFieldValue('topicIds'),
                                        topics,
                                        directions,
                                        projectsListData,
                                    });
                                    const buttonColLayout = computeButtonColLayout(visibleFieldCount);

                                    return (
                                        <Col {...buttonColLayout}>
                                            <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                                <Space size="small">
                                                    <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                                        检索
                                                    </Button>
                                                    <Button type="default" onClick={onReset}>
                                                        重置
                                                    </Button>
                                                    {expandedFieldCount > collapsedFieldCount && (
                                                        <Button type="link" size="small" aria-expanded={expanded} onClick={() => setExpanded(prev => !prev)} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                                            {expanded ? (
                                                                <>
                                                                    收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </Space>
                                            </Form.Item>
                                        </Col>
                                    );
                                }}
                            </Form.Item>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container selection-cases-table-region">
                <div className="main-toolbar selection-cases-toolbar">
                    <div className="main-tools">
                        <div className="table-title">实践列表</div>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={getData} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap selection-cases-table-wrap">
                    <Table
                        size="medium"
                        bordered
                        className={tableScrollY != null ? 'custom-table-scrollbar-always enterprises-table' : 'enterprises-table'}
                        rowKey={item => item.id}
                        columns={columns}
                        dataSource={data}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        scroll={{ x: 1300, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangePage,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
